/* KBW Vanity PDP — KBW-90 — countertop selector

   A vanity's nine countertop choices are nine real Shopify variants of the
   one product, not nine sibling products — unlike the cabinet page's width
   and finished-end pickers (assets/kbw-pdp.js), which move to a different
   product's own URL. Choosing a countertop here never navigates: it swaps
   the price, compare-at price, SKU and availability shown on this page and
   points the existing Add to cart form (already on the page, unchanged) at
   the chosen variant.

   Everything this needs is already rendered server side, one attribute set
   per chip — no fetch, no template swap. kbw-pdp.js still owns the gallery,
   the quantity stepper and the add-to-cart submit; this file only owns the
   nine buttons.

   One more thing this owns, added after a proven cart test: the Maximize
   theme's own quick-view shell (sections/quick-view.liquid) is rendered on
   every product page, whether or not it is ever opened, and defaults to the
   page's own product — so it carries a second, hidden `form.product-info__
   form` with its own `input[name="id"]`, pre-set to this product's default
   variant. On a cabinet or a flooring box that field can never go wrong,
   because there is only one variant to be. On a vanity it can: it never
   heard about a countertop change, so it keeps naming "No Countertop" no
   matter what a shopper picked. Nothing customer-facing submits it — a
   direct end-to-end test (click a stone, click the real Add to cart button,
   read /cart.js) came back with the correct variant, price and SKU — but
   nothing on the page may be allowed to disagree with what was chosen, so
   every hidden `id` field naming one of this product's own nine variants is
   kept in sync below, not only the one this file rendered.

   Custom section script — not part of the Maximize theme. */

(function () {
  'use strict';

  var root = document.querySelector('[data-kbw-vanity-pdp]');
  if (!root) return;

  var list = root.querySelector('[data-kbw-pdp-countertop-list]');
  if (!list) return;

  var chips = [].slice.call(list.querySelectorAll('[data-kbw-pdp-countertop]'));
  if (!chips.length) return;

  // Every variant id this product has. Used only to recognise a stray
  // `input[name="id"]` elsewhere on the page as a mirror of THIS product
  // (the quick-view shell, today) rather than touching a field that
  // belongs to something else entirely.
  var variantIds = chips
    .map(function (c) { return c.getAttribute('data-variant-id'); })
    .filter(Boolean);

  function syncStrayIdFields(variantId) {
    [].slice.call(document.querySelectorAll('input[name="id"]')).forEach(function (field) {
      if (variantIds.indexOf(field.value) !== -1) field.value = variantId;
    });
  }

  // The gallery is rendered by sections/kbw-vanity-pdp.liquid: one
  // `[data-kbw-pdp-shot]` per photo and, when there is more than one, a
  // matching `[data-kbw-pdp-thumb]`. kbw-pdp.js owns the thumbnail clicks;
  // this moves the same two classes, so a countertop chip and a thumbnail
  // leave the gallery in exactly the same state.
  //
  // An index that names no shot — a countertop whose photo is not among the
  // first twelve, or the far commoner case today of a countertop with no
  // photo at all — falls back to the first one, which is what the shopper
  // was looking at before they chose anything.
  function showShot(index) {
    var shots = [].slice.call(root.querySelectorAll('[data-kbw-pdp-shot]'));
    if (shots.length < 2) return;
    var exists = shots.some(function (s) { return s.getAttribute('data-kbw-pdp-shot') === index; });
    var wanted = exists ? index : '0';
    shots.forEach(function (s) {
      s.classList.toggle('is-on', s.getAttribute('data-kbw-pdp-shot') === wanted);
    });
    [].slice.call(root.querySelectorAll('[data-kbw-pdp-thumb]')).forEach(function (t) {
      t.classList.toggle('is-on', t.getAttribute('data-kbw-pdp-thumb') === wanted);
    });
  }

  function select(chip) {
    // A native `disabled` button never fires a click, but the guard stays —
    // this function is the one place "do not let it be chosen" is enforced,
    // and a future caller should not have to know why the click never
    // happens on its own.
    if (chip.disabled || chip.getAttribute('data-available') === 'false') return;

    chips.forEach(function (c) { c.classList.toggle('is-on', c === chip); });

    var priceText = chip.getAttribute('data-price-text') || '';
    var priceEl = root.querySelector('[data-kbw-pdp-price]');
    if (priceEl) priceEl.textContent = priceText;

    var saveText = chip.getAttribute('data-save-text') || '';
    var aside = root.querySelector('[data-kbw-pdp-aside]');
    var wasEl = root.querySelector('[data-kbw-pdp-was]');
    var saveEl = root.querySelector('[data-kbw-pdp-save]');
    if (aside) {
      aside.hidden = saveText === '';
      if (wasEl) wasEl.textContent = chip.getAttribute('data-compare-at-text') || '';
      if (saveEl) saveEl.textContent = 'Save ' + saveText;
    }

    var sku = chip.getAttribute('data-sku') || '';
    // Every place the SKU is printed on the page — the buy box, the gallery
    // caption, the specification grid, and the breadcrumb when it renders —
    // moves together, so nothing on screen still names the countertop the
    // shopper just moved away from.
    ['[data-kbw-pdp-sku]', '[data-kbw-pdp-sku-caption]', '[data-kbw-pdp-sku-fact]', '[data-kbw-pdp-sku-crumb]']
      .forEach(function (sel) {
        var el = root.querySelector(sel);
        if (el) el.textContent = sku;
      });

    var variantId = chip.getAttribute('data-variant-id') || '';
    var idEl = root.querySelector('[data-kbw-pdp-id]');
    if (idEl) idEl.value = variantId;
    if (variantId) syncStrayIdFields(variantId);

    var available = chip.getAttribute('data-available') === 'true';

    var cta = root.querySelector('[data-kbw-pdp-cta]');
    if (cta) {
      cta.disabled = !available;
      cta.textContent = available ? 'Add to cart' : 'Out of stock';
    }

    var badge = root.querySelector('[data-kbw-pdp-stockbadge]');
    if (badge) {
      badge.classList.toggle('is-in', available);
      badge.classList.toggle('is-out', !available);
      badge.textContent = available ? 'In stock' : 'Out of stock';
    }

    // The picture follows the choice. `data-media-index` is empty for a
    // countertop with no photo of its own, and showShot then returns to the
    // first shot rather than leaving the previous stone on screen.
    var mediaIndex = chip.getAttribute('data-media-index');
    showShot(mediaIndex === null || mediaIndex === '' ? '0' : mediaIndex);
  }

  chips.forEach(function (chip) {
    chip.addEventListener('click', function () { select(chip); });
  });
}());
